import { useMemo, useCallback, useState } from 'react'
import useAppAuth from '../../AppAuthProvider/hooks/useAppAuth'
import {
    ActivationObject,
    ActivationObjectProviderProps,
    ActivationObjectProviderValue,
    useActivationObjectProviderProps,
} from '../types'
import {
    useActivationObjectCreateMutation,
    useActivationObjectQuery,
    useActivationObjectUpdateMutation,
    useFinishMutation,
} from '../../../../api/activationObject/hooks'
import { ActivationBody } from '../../../../api/activationObject/types'

const useActivationObjectProvider = (props: useActivationObjectProviderProps): ActivationObjectProviderProps => {
    const auth = useAppAuth()
    const { data: activationObject, isError } = useActivationObjectQuery(auth.synonym)
    const updateMutation = useActivationObjectUpdateMutation()
    const createMutation = useActivationObjectCreateMutation()
    const finishMutation = useFinishMutation()
    const [finishPcoObject, setFinishPcoObject] = useState(false)

    const error = useMemo(() => {
        if (createMutation.error) return 'Chyba při zakládání aktivačního objektu: ' + createMutation.error.title
        if (updateMutation.error) return 'Chyba při aktualizaci aktivačního objektu: ' + updateMutation.error.title
    }, [createMutation.error, updateMutation.error])

    const create = useCallback(
        ({ isExchangeForNew }: Pick<ActivationBody, 'isExchangeForNew'>) => {
            if (auth.synonym) {
                createMutation.mutate({ synonym: auth.synonym, isExchangeForNew })
            }
        },
        [auth.synonym, createMutation]
    )

    const finish = useCallback(() => {
        if (auth.synonym) {
            if (finishPcoObject) {
                finishMutation.mutate(auth.synonym)
                setFinishPcoObject(false)
            } else {
                create({ isExchangeForNew: false })
                setFinishPcoObject(true)
            }
        }
    }, [auth.synonym, create, finishMutation, finishPcoObject])

    const update = useCallback(
        (obj: ActivationObject) => {
            updateMutation.mutate(obj)
        },
        [updateMutation]
    )

    const contextValue: ActivationObjectProviderValue = {
        data: activationObject,
        create,
        finish,
        update,
        finishPcoObject,
        isUpdating: updateMutation.isLoading || createMutation.isLoading,
    }

    return {
        contextValue,
        error,
        notFound: isError,
    }
}

export default useActivationObjectProvider
